import {
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducer';
import { ConfirmationService } from 'primeng/api';
import { findIndex } from 'lodash';
import { update } from 'lodash/fp';
import {
  deleteContentItemToProject,
  loadProjectContentItems,
} from 'app/redux/actions';
import { ContentItems, SidebarList } from 'app/models';
import { Content } from 'app/constants';
import { ContentItemsService } from 'app/services';
import { ToastNotificationService } from 'app/components';
import { combineLatest } from 'rxjs';

@Component({
  selector: 'app-sidebar-content-items-list',
  template: `
    <app-sidebar-entity-table
      kind="article"
      [items]="contentItems"
      (unfollow)="unfollow($event)"
    ></app-sidebar-entity-table>
  `,
})
export class SidebarContentItemsListComponent implements OnInit {
  @Output() createNewContentItemOnclick: EventEmitter<any> = new EventEmitter();
  @Output() editContentItemOnclick: EventEmitter<any> = new EventEmitter();
  @Output() selectedContentItemOnClick: EventEmitter<any> = new EventEmitter();
  public selectTopic: any;
  public contentItems: SidebarList[];
  private allContentItems: SidebarList[];
  public sort: any;
  public showScrollbar: boolean;
  public isEdit: boolean;
  public isUnfollow: boolean;
  public currentProject: any;
  public userId: any;
  public parentName: string;
  public reordering: boolean;

  @ViewChildren('sortNameIcon, sortCategoryIcon', {
    read: ElementRef,
  })
  sortIcons: QueryList<ElementRef>;

  constructor(
    private store: Store<AppState>,
    private confirmationService: ConfirmationService,
    private contentItemsService: ContentItemsService,
    private notificationService: ToastNotificationService
  ) {
    this.contentItems = [];
    this.allContentItems = [];
    this.showScrollbar = false;
    this.isEdit = false;
    this.isUnfollow = false;
    this.userId = null;
    this.parentName = Content.ContentItem;
    this.reordering = false;
  }

  ngOnInit() {
    this.store.select('userData').subscribe(({ data }) => {
      if (data) {
        this.userId = data.userId;
      }
    });

    combineLatest([
      this.store.select('contentItemsList'),
      this.store.select('authorsOutletsList'),
    ]).subscribe(([{ contentItems, loaded }, { outlets }]) => {
      if (contentItems && contentItems.length > 0 && loaded) {
        this.allContentItems = contentItems.map((item) => {
          const outlet = outlets.find(
            (candidate) => candidate.partyId === item.outletId
          );
          const label = outlet?.label?.name;
          return new SidebarList({
            title: item.title,
            publishDate: item.publishDate,
            subTitle: `${item.outlet}`,
            picture: null,
            hasPicture: true,
            parentObject: { ...item, categoryName: label },
            parentName: this.parentName,
          });
        });
        const visibleContentItems = this.allContentItems.filter(
          (item) => item.parentObject.categoryName !== 'Ignore'
        );
        this.contentItems = [
          ...visibleContentItems.filter(
            (item) => !!item.parentObject.categoryName
          ),
          ...visibleContentItems.filter(
            (item) => !item.parentObject.categoryName
          ),
        ];
      } else {
        this.allContentItems = [];
        this.contentItems = [];
      }
      this.showScrollbar = this.contentItems.length > 10;
    });

    this.store.select('project').subscribe(({ projectSelected }) => {
      if (projectSelected) {
        this.currentProject = projectSelected;
      }
    });
  }

  public handleCreateNewContentItem() {
    this.createNewContentItemOnclick.emit(null);
  }

  public handleEditContentItem(index) {
    this.editContentItemOnclick.emit(index);
  }

  public selectedContentItem(item) {
    this.selectedContentItemOnClick.emit(item);
  }

  public reorderContentItems(reordered: SidebarList[]) {
    const previous = this.contentItems;
    this.contentItems = reordered;
    this.reordering = true;

    const reorderedItems = [...reordered];
    const completeOrder = this.allContentItems.map((item) =>
      item.parentObject.categoryName !== 'Ignore'
        ? reorderedItems.shift()
        : item
    );
    const orderedIds = completeOrder.map((item) => item.parentObject.id);
    this.contentItemsService
      .updateContentItemOrder(this.currentProject.value, orderedIds)
      .subscribe(
        () => {
          this.reordering = false;
          this.store.dispatch(
            loadProjectContentItems({ projectId: this.currentProject.value })
          );
        },
        () => {
          this.contentItems = previous;
          this.reordering = false;
          this.notificationService.showMessage(
            'Unable to save article order.',
            'Error'
          );
        }
      );
  }

  handleUnfollow(value) {
    this.confirmationService.confirm({
      message: 'Are you sure that you want to unfollow this content item?',
      header: 'Unfollow Confirmation',
      icon: 'pi pi-exclamation-triangle',
      key: 'unfollowConfirmation',
      accept: () => {
        this.unfollow(value);
      },
      reject: (type) => {
        // handle rejected option ConfirmEventType.CANCEL
      },
    });
  }

  public unfollow(value) {
    this.store.dispatch(
      deleteContentItemToProject({
        projectId: this.currentProject.value,
        contentItemId: value.id,
      })
    );
  }
}
